import { useEffect } from 'react'

import { NodeEventPlugin } from '@lexical/react/LexicalNodeEventPlugin'
import {
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_EDITOR
} from 'lexical'

import { pick } from '@eastcoal/common/utils/object'

import { useEditor } from '../../hooks/useEditor'
import { useMutationListener } from '../../hooks/useMutationListener'
import {
  $createLinkNode,
  $isLinkNode,
  LinkNode
} from '../../nodes/LinkNode/index'
import { LinkCard } from '../../ui/LinkCard'
import { useLinkCardStore } from '../../ui/LinkCard/Store/hooks/useStore'

import { INSERT_LINK_COMMAND, TOGGLE_LINK_COMMAND } from './commands'
import { ToggleLinkPayload } from './interface'

const toggleLink = (options: ToggleLinkPayload) => {
  const selection = $getSelection()

  if (!$isRangeSelection(selection)) {
    return
  }

  const nodes = selection.extract()

  // 取消链接
  if (options === false) {
    nodes.forEach(node => {
      const parent = node.getParentOrThrow()
      if ($isLinkNode(parent)) {
        const textNode = $createTextNode(parent.getTextContent())
        parent.replace(textNode)
      }
    })
    return
  }

  options = options === true ? {} : options

  // 设置链接
  if (nodes.length === 1) {
    const firstNode = nodes[0]
    if ($isLinkNode(firstNode)) {
      firstNode.setState({
        ...firstNode.getState(),
        ...options
      })
    } else {
      const linkNode = $createLinkNode({
        text: firstNode.getTextContent(),
        ...options
      })
      firstNode.replace(linkNode)
    }
    return
  }

  // 多个节点合并成一个链接
  const linkNode = nodes.find($isLinkNode) ?? $createLinkNode(options)
  const text = options.text ?? selection.getTextContent()
  linkNode.setTextContent(text)
  nodes.forEach((node, index) => {
    if (index === 0) {
      node.replace(linkNode)
    } else {
      node.remove()
    }
  })
}

const LinkPluginCore = () => {
  const editor = useEditor()
  const { openForm, closeForm, openToolbar, closeToolbar } = useLinkCardStore(
    state =>
      pick(state, [
        'openForm',
        'closeForm',
        'openToolbar',
        'closeToolbar',
        'closeToolbar'
      ])
  )

  // 设置/取消链接
  useEffect(() => {
    return editor.registerCommand(
      TOGGLE_LINK_COMMAND,
      payload => {
        toggleLink(payload)
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor])

  // 插入链接
  useEffect(() => {
    if (!editor.hasNodes([LinkNode])) {
      throw new Error('LinkPlugin: LinkNode not registered on editor')
    }
    return editor.registerCommand(
      INSERT_LINK_COMMAND,
      payload => {
        const selection = $getSelection()
        if (
          !$isRangeSelection(selection) ||
          selection.anchor.offset === selection.focus.offset
        ) {
          // 插入默认链接
          const linkNode = $createLinkNode(
            payload ?? {
              url: '',
              text: '链接'
            }
          )
          $insertNodes([linkNode])
          return false
        }

        const nodes = selection.extract()
        // 禁止把多个节点转换成链接
        if (nodes.length !== 1) {
          return false
        }
        const selectedNode = nodes[0]
        const parentNode = selectedNode.getParentOrThrow()
        // 如果是链接节点，打开编辑器
        if ($isLinkNode(parentNode)) {
          openForm(parentNode.getKey())
          return false
        }

        // 如果是文本节点，转换成链接节点
        if ($isTextNode(selectedNode)) {
          const linkNode = $createLinkNode({
            text: selectedNode.getTextContent()
          })
          selectedNode.replace(linkNode)
          return false
        }

        return false
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor, openForm])

  // 节点创建时打开交互组件
  useMutationListener(LinkNode, 'created', nodeKey => {
    editor.getEditorState().read(() => {
      const node = $getNodeByKey(nodeKey)
      if ($isLinkNode(node) && node.isManualInsert()) {
        openForm(nodeKey)
      }
    })
  })

  // 节点被移除时关闭交互组件
  useMutationListener(LinkNode, 'destroyed', nodeKey => {
    closeForm(nodeKey)
    closeToolbar(nodeKey)
  })

  // 节点为空时替换成文本节点
  useMutationListener(LinkNode, 'updated', nodeKey => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey)
      if ($isLinkNode(node) && node.isEmpty()) {
        node.replace($createTextNode())
      }
    })
  })

  return (
    <NodeEventPlugin
      eventType='mouseover'
      nodeType={LinkNode}
      eventListener={(_, __, nodeKey) => {
        openToolbar(nodeKey)
      }}
    />
  )
}

export const LinkPlugin = () => {
  return (
    <LinkCard>
      <LinkPluginCore />
    </LinkCard>
  )
}
